import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { Button, Form, Nav, Tab } from 'react-bootstrap';
import UseFontUploader from '../../hooks/fontUploader/useFontUploader';
import { FontService } from '@services/font/FontService';
import type { FontFamily } from '@services/font/commands/cmdGetFontFamilies';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';

const FontUploader: React.FC<any> = () => {
    useDocumentTitle('Font Uploader');
    const { uploadFont, uploadSuccess } = UseFontUploader();
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [availableFonts, setAvailableFonts] = useState<FontFamily[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');

    useEffect(() => {
        const fontService = new FontService();
        const fetchAvailableFonts = async () => {
            try {
                const fonts = await fontService.getFontFamilies('uploaded');
                setAvailableFonts(fonts);
            } catch (error) {
                console.error('Error fetching available fonts:', error);
            }
        };

        fetchAvailableFonts();
    }, [uploadSuccess]);

    const handleFileSelection = (files: FileList) => {
        const validFiles = Array.from(files).filter(file =>
            file.name.endsWith('.ttf') || file.name.endsWith('.otf') || file.name.endsWith('.ttc')
        );
        setSelectedFiles(prevFiles => [...prevFiles, ...validFiles]);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (e.target.files) {
            handleFileSelection(e.target.files);
        }
    };

    const handleFolderChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (e.target.files) {
            handleFileSelection(e.target.files);
        }
    };

    const handleRemoveFile = (index: number) => {
        setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    };

    const handleUpload = (): void => {
        if (selectedFiles.length > 0) {
            selectedFiles.forEach(file => uploadFont(file));
            setSelectedFiles([]); // Clear after upload
        }
    };

    // Filter families by search query, then list each family's variants by weight/style.
    const groupedFonts = useMemo(() => {
        const query = searchQuery.toLowerCase();
        return availableFonts
            .filter(family => family.family_name.toLowerCase().includes(query))
            .reduce((groups: { [family: string]: string[] }, family) => {
                groups[family.family_name] = family.variants.map(v =>
                    `${family.family_name} ${v.weight}${v.italic ? ' Italic' : ''} (${v.format})`
                );
                return groups;
            }, {});
    }, [availableFonts, searchQuery]);

    return (
        <Fragment>
            <div className="mx-auto px-5 py-3 bg-white vh-100 overflow-hidden d-flex justify-content-between flex-column">
                <div className="d-flex flex-column">
                    <h4 className="my-0 py-0 text-center mb-2 mt-3">Upload Fonts</h4>
                    <Tab.Container defaultActiveKey="first">
                        <Nav className="nav nav-tabs mb-3 border-0 nav-style-1">
                            <Nav.Item>
                                <Nav.Link href="#project" eventKey="first">
                                    Font Uploader
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link href="#available" eventKey="second">
                                    Available Fonts
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tab.Content>
                            <Tab.Pane className="tab-pane show text-muted" id="project" role="tabpanel" eventKey="first">
                                {/* Upload Buttons */}
                                <div className="d-flex flex-column align-items-center">
                                    <Form.Control
                                        type="file"
                                        accept=".ttf,.otf,.ttc"
                                        multiple
                                        onChange={handleFileChange}
                                        className="d-none"
                                        id="file-input"
                                    />
                                    <Form.Control
                                        type="file"
                                        onChange={handleFolderChange}
                                        {...({ webkitdirectory: "" } as any)}
                                        className="d-none"
                                        id="folder-input"
                                    />
                                    <div className="d-flex gap-2 justify-content-center">
                                        <label htmlFor="file-input" className="btn btn-primary">
                                            Select Files
                                        </label>
                                        <label htmlFor="folder-input" className="btn btn-secondary">
                                            Select Folder
                                        </label>
                                    </div>
                                </div>

                                {/* File List with Min Height & Placeholder Text */}
                                <div
                                    className="mt-3 file-list-container border rounded p-3 d-flex flex-column justify-content-center align-items-center"
                                    style={{ maxHeight: '400px', minHeight: '100px', overflowY: 'auto' }}
                                >
                                    {selectedFiles.length === 0 ? (
                                        <p className="text-muted text-center">
                                            No fonts selected yet.
                                            <br />
                                            Choose files or a folder to begin.
                                        </p>
                                    ) : (
                                        <ul className="list-group w-100">
                                            {selectedFiles.map((file, index) => (
                                                <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                                    {file.name}
                                                    <button
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={(e) => {
                                                            e.currentTarget.blur();
                                                            handleRemoveFile(index);
                                                        }}
                                                    >
                                                        ✖
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                {/* Upload Button */}
                                {selectedFiles.length > 0 && (
                                    <Button variant="success" className="mt-3 w-100" onClick={handleUpload}>
                                        Upload {selectedFiles.length} {selectedFiles.length > 1 ? 'Files' : 'File'}
                                    </Button>
                                )}
                                {uploadSuccess && (
                                    <div className="alert alert-success text-center mt-2" role="alert">
                                        Fonts uploaded successfully! 🎉
                                    </div>
                                )}
                            </Tab.Pane>

                            <Tab.Pane className="tab-pane show text-muted" id="available" role="tabpanel" eventKey="second">
                                <h5 className="text-center mb-3">Available Fonts</h5>
                                <Form.Control
                                    type="text"
                                    placeholder="Search fonts..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="mb-3"
                                />
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {Object.keys(groupedFonts).length === 0 ? (
                                        <p className="text-muted text-center">No fonts available.</p>
                                    ) : (
                                        Object.keys(groupedFonts)
                                            .sort()
                                            .map((family) => (
                                                <div key={family} className="mb-3">
                                                    <h6>{family}</h6>
                                                    <ul className="list-group">
                                                        {groupedFonts[family].map((font, index) => (
                                                            <li key={index} className="list-group-item">
                                                                {font}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))
                                    )}
                                </div>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </div>
            </div>
        </Fragment>
    );
};

export default FontUploader;
